import React, { createContext, useState, useCallback } from 'react'
import { useConfig } from './useConfig'

export type ConnectionMode = 'cloud' | 'manual' | 'env'

type ConnectionData = {
  shouldConnect: boolean
  wsUrl: string
  token: string
  mode: ConnectionMode
  disconnect: () => Promise<void>
  connect: (mode: ConnectionMode) => Promise<void>
}

const ConnectionContext = createContext<ConnectionData | undefined>(undefined)

export const ConnectionProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { config } = useConfig()
  const [connectionDetails, setConnectionDetails] = useState<{
    wsUrl: string
    token: string
    mode: ConnectionMode
    shouldConnect: boolean
  }>({ wsUrl: '', token: '', shouldConnect: false, mode: 'manual' })

  const connect = useCallback(
    async (mode: ConnectionMode) => {
      console.log('[useConnection] connect() called with mode:', mode)
      console.log('[useConnection] config.settings:', config.settings)
      let token = ''
      let url = ''

      if (mode === 'env') {
        // Use URL from config (passed from parent app)
        url = config.settings.ws_url
        console.log('[useConnection] LiveKit URL from config:', url)
        if (!url) {
          throw new Error(
            'LiveKit URL is not configured. Please set VITE_LIVEKIT_URL in your .env file.',
          )
        }

        // Build request body from config settings
        const body: Record<string, any> = {}

        // Use defaults if not set in config
        body.roomName =
          config.settings.room_name ||
          `playground-${Math.random().toString(36).substring(2, 6)}-${Math.random().toString(36).substring(2, 6)}`
        body.participantName =
          config.settings.participant_name ||
          `user-${Math.random().toString(36).substring(2, 6)}`

        if (config.settings.participant_id) {
          body.participantId = config.settings.participant_id
        }
        if (config.settings.agent_name) {
          body.agentName = config.settings.agent_name
        }
        if (config.settings.metadata) {
          body.metadata = config.settings.metadata
        }

        const attributesArray = Array.isArray(config.settings.attributes)
          ? config.settings.attributes
          : []
        if (attributesArray?.length) {
          const attributes = attributesArray.reduce(
            (acc, attr) => {
              if (attr.key) {
                acc[attr.key] = attr.value
              }
              return acc
            },
            {} as Record<string, string>,
          )
          body.attributes = attributes
        }

        // Call our backend API to generate token
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:6060'
        console.log(
          '[useConnection] Calling API:',
          `${apiUrl}/api/livekit/token`,
        )
        console.log('[useConnection] Request body:', body)

        const response = await fetch(`${apiUrl}/api/livekit/token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify(body),
        })

        console.log('[useConnection] API response status:', response.status)

        if (!response.ok) {
          const errorText = await response.text()
          console.error('[useConnection] API error response:', errorText)
          throw new Error(`Failed to generate token: ${response.statusText}`)
        }

        const responseData = await response.json()
        console.log('[useConnection] API response data:', responseData)

        // Handle NestJS response wrapper (data.data.accessToken)
        const data = responseData.data || responseData
        token = data.accessToken || data.token
        console.log(
          '[useConnection] Extracted token:',
          token ? 'Token received' : 'No token!',
        )
      } else {
        // Manual mode: use URL and token from config settings
        token = config.settings.token
        url = config.settings.ws_url
      }

      console.log('[useConnection] Setting connection details:', {
        wsUrl: url,
        token: token ? 'Token set' : 'NO TOKEN',
        shouldConnect: true,
        mode,
      })
      setConnectionDetails({ wsUrl: url, token, shouldConnect: true, mode })
    },
    [config.settings],
  )

  const disconnect = useCallback(async () => {
    setConnectionDetails(prev => ({ ...prev, shouldConnect: false }))
  }, [])

  return (
    <ConnectionContext.Provider
      value={{
        wsUrl: connectionDetails.wsUrl,
        token: connectionDetails.token,
        shouldConnect: connectionDetails.shouldConnect,
        mode: connectionDetails.mode,
        connect,
        disconnect,
      }}
    >
      {children}
    </ConnectionContext.Provider>
  )
}

export const useConnection = () => {
  const context = React.useContext(ConnectionContext)
  if (context === undefined) {
    throw new Error('useConnection must be used within a ConnectionProvider')
  }
  return context
}
